<template>
  <v-content class="ma-0 pa-0">
    <v-dialog
      v-model="openDialog"
      width="1000"
      persistent
      @keydown.esc="openDialog = false"
    >
      <v-card block flat>
        <v-card-title class="title pb-0">
          <span class="title">Send Document Drop Off</span>
          <v-spacer />
          <span class="caption error--text">*Required</span>
        </v-card-title>
        <v-card-text>
          <v-alert
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="info"
            class="text-xs-left mb-2"
          >
            Fill out the following fields to send Document Drop Off. Links
            expire within 7 days and clients cannot leave notes.
          </v-alert>
          <v-layout wrap class="text-xs-left">
            <v-flex xs4 class="px-2" id="entities-select-field">
              <div>Send to<span class="error--text ml-1">*</span></div>

              <v-autocomplete
                multiple
                class="default-input body-1"
                hide-details
                small-chips
                :loading="loadingCompanies"
                :disabled="loadingCompanies || !multiple"
                solo
                :items="entitiesWithClient"
                v-model="entityIds"
                :placeholder="multiple ? 'Select entities' : ''"
                item-value="id"
                item-text="name"
              >
                <template slot="selection" slot-scope="data">
                  <v-chip small v-if="entityIds.indexOf(data.item.id) === 0">
                    <span>{{ data.item.name }}</span>
                  </v-chip>
                  <span
                    v-if="entityIds.indexOf(data.item.id) === 1"
                    class="grey--text caption"
                    >(+{{ entityIds.length - 1 }} other{{
                      entityIds.length > 2 ? 's' : ''
                    }})</span
                  >
                </template>
                <template slot="item" slot-scope="data">
                  <v-layout column align-start>
                    <v-layout align-center
                      >{{ data.item.name }}
                      <v-chip small v-if="data.item.for_testing">
                        <span>Test</span>
                      </v-chip>
                      <v-chip
                        small
                        class="error white--text"
                        v-if="!data.item.active"
                      >
                        <span>Inactive</span>
                      </v-chip>
                    </v-layout>
                    <!-- <div class="darkgrey--text caption">{{data.item.email}}</div> -->
                  </v-layout>
                </template>
              </v-autocomplete>
              <v-layout v-if="!loadingCompanies && multiple" align-start wrap>
                <v-flex
                  shrink
                  no-wrap
                  v-for="type in entityOptionsWithoutAll"
                  :key="type"
                  class="primary--text caption mx-2 mb-1"
                  style="cursor: pointer"
                  @click="selectSpecificEntityType(type)"
                  >{{ type }}{{ type === 'All' ? '' : 's' }}</v-flex
                >
                <v-flex
                  shrink
                  no-wrap
                  v-if="entitiesWithClient.some(c => c.for_testing)"
                  key="test"
                  class="primary--text caption mx-2 mb-1"
                  style="cursor: pointer"
                  @click="selectSpecificEntityType('Test')"
                  >Test Clients</v-flex
                >
                <v-flex
                  shrink
                  no-wrap
                  v-if="!isEmpty(entityIds)"
                  class="error--text caption mx-2 mb-1"
                  style="cursor: pointer"
                  @click="entityIds = []"
                  >Clear</v-flex
                >
              </v-layout>
            </v-flex>
            <v-flex xs4 class="px-2">
              <div>Send As</div>
              <v-text-field
                class="default-input body-1"
                solo
                optional
                v-model="customTemplateName"
              />
            </v-flex>
            <v-flex
              xs4
              id="year-select-field"
              v-if="selectedFrequency === 'once' || !isScheduledTemplate"
              class="px-2"
            >
              <div>
                Tax Year<span class="error--text ml-1">*</span
                ><v-tooltip top
                  ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                  ><span
                    >Specify which folder documents should be organized
                    into</span
                  ></v-tooltip
                >
              </div>
              <v-autocomplete
                class="default-input body-1"
                solo
                :rules="rules.presence"
                :items="yearOptions"
                v-model="questionnaireYear"
                placeholder="Select a tax year"
              />
            </v-flex>
            <v-flex xs4 id="year-select-field" v-else class="px-2">
              <div>
                Tax Year<span class="error--text ml-1">*</span
                ><v-tooltip top
                  ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                  ><span
                    >Specify which folder documents should be organized
                    into</span
                  ></v-tooltip
                >
              </div>
              <v-text-field
                disabled
                class="default-input body-1"
                solo
                value="Year of send date"
              />
            </v-flex>
            <!-- <v-flex xs4 v-if="isScheduledTemplate" class="px-2">
              <div slot="activator">
                Frequency
                <v-tooltip top>
                  <v-icon slot="activator" size="15" class="ml-1">info</v-icon>
                  <span style="font-size: 15px">
                    Specify the number of times to send the questionnaire within
                    a period of time.<br />
                    <b
                      ><span
                        >Please note that this is not a setting for sending
                        reminders.</span
                      ></b
                    >
                  </span>
                </v-tooltip>
              </div>
              <v-autocomplete
                class="default-input body-1"
                solo
                :items="templateFrequencyOptions"
                item-text="text"
                item-value="value"
                v-model="selectedFrequency"
              />
            </v-flex>
            <v-flex xs4 v-if="isScheduledTemplate" class="px-2">
              <div>
                Send Date<v-tooltip top
                  ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                  ><span
                    >The scheduled questionnaire will be sent out at 9AM in your
                    timezone</span
                  ></v-tooltip
                >
              </div>
              <v-text-field
                class="default-input body-1"
                type="date"
                solo
                item-text="name"
                item-value="id"
                :rules="rules.sendDate"
                v-model="questionnaireScheduledDate"
                placeholder="Choose a due date"
              />
            </v-flex>
            <v-flex xs4 v-if="isScheduledTemplate" class="px-2">
              <div>
                Days until Due<v-tooltip top
                  ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                  ><span
                    >Reminders will be sent out automatically to incomplete
                    questionnaire approaching the due date</span
                  ></v-tooltip
                >
              </div>
              <v-text-field
                class="default-input body-1"
                solo
                :rules="rules.positiveNumber"
                v-model="questionnaireDaysUntilDue"
                placeholder="Select how many days until due"
              />
            </v-flex> -->
            <v-flex xs4 class="px-2">
              <div>
                Due Date<v-tooltip top
                  ><v-icon slot="activator" size="15" class="ml-1">info</v-icon
                  ><span
                    >Reminders will be sent out automatically to incomplete
                    questionnaire approaching the due date</span
                  ></v-tooltip
                >
              </div>
              <v-text-field
                class="default-input body-1"
                type="date"
                solo
                :rules="rules.dueDate"
                v-model="questionnaireDueDate"
                placeholder="Choose a due date"
              />
            </v-flex>
            <v-flex xs12 class="px-2 mb-4">
              Options
              <v-layout>
                <v-checkbox
                  v-model="includeRolloverItems"
                  class="my-0 pa-0 shrink"
                  label="Include Rollover Items"
                  hide-details
                />
                <v-tooltip bottom>
                  <v-icon slot="activator" size="15" class="ml-1">info</v-icon>
                  <span>The checklist will include rollover items</span>
                </v-tooltip>
              </v-layout>
              <v-layout>
                <v-checkbox
                  v-model="addAdditionalQuestions"
                  label="Add Additional Items"
                  class="my-0 pa-0 shrink"
                  hide-details
                />
                <v-tooltip bottom>
                  <v-icon slot="activator" size="15" class="ml-1">info</v-icon>
                  <span>Added items will not be saved as rollover items</span>
                </v-tooltip>
              </v-layout>
              <v-layout>
                <v-checkbox
                  v-model="customEmailEnabled"
                  label="Custom Email"
                  class="my-0 pa-0 shrink"
                  hide-details
                />
                <v-tooltip bottom>
                  <v-icon slot="activator" size="15" class="ml-1">info</v-icon>
                  <span>Customize initial email subject and message</span>
                </v-tooltip>
              </v-layout>
            </v-flex>
            <v-flex v-if="customEmailEnabled" xs12 class="px-2 mb-4">
              <span>Initial Email Subject</span>
              <v-text-field
                class="default-input"
                solo
                v-model="customEmailSubject"
              ></v-text-field>
              <span>Initial Email Body</span>
              <v-textarea
                class="default-input"
                solo
                v-model="customEmailBody"
              ></v-textarea>
            </v-flex>
          </v-layout>
          <v-layout column class="px-2" v-if="entityIds.length === 1">
            <v-layout align-center v-if="priorYearItemsTrigger">
              Rollover Items for {{ questionnaireYear }}
              <v-tooltip top>
                <v-icon slot="activator" size="15" class="ml-1">info</v-icon>
                <span>These documents will be part of the checklist items</span>
              </v-tooltip>
            </v-layout>

            <PriorYearItems
              ref="priorYearItems"
              :year="questionnaireYear"
              :company="selectedEntity"
              :opened="priorYearItemsTrigger"
            />
          </v-layout>
          <v-layout
            column
            class="text-xs-left px-2"
            v-if="addAdditionalQuestions"
          >
            <v-layout class="mb-2"> Additional Items </v-layout>
            <v-layout
              class="text-xs-left mb-4"
              align-center
              v-for="(question, index) in questionsToCreate"
              :key="index"
            >
              <v-layout column>
                <v-layout align-center class="mb-2">
                  <span class="title mr-1">{{ index + 1 }}. </span>
                  <!-- <v-flex shrink class="mx-4 text-xs-left">
                    <v-btn-toggle mandatory v-model="question.question_type">
                      <v-btn flat value="text" class="text-none caption">
                        Question
                      </v-btn>
                      <v-btn flat value="upload" class="text-none caption">
                        Document Request
                      </v-btn>
                    </v-btn-toggle>
                  </v-flex> -->
                  <v-text-field
                    v-model="question.text"
                    solo
                    hide-details
                    class="default-input"
                    :placeholder="
                      question.question_type === 'upload'
                        ? 'DOCUMENT NAME'
                        : 'Please explain...'
                    "
                  />
                </v-layout>
              </v-layout>
              <v-tooltip
                bottom
                class="ml-3"
                v-if="questionsToCreate.length !== 1"
              >
                <i
                  class="material-icons-outlined error--text"
                  slot="activator"
                  @click="questionsToCreate.splice(index, 1)"
                  style="cursor: pointer"
                  >delete</i
                >
                <span>Delete</span>
              </v-tooltip>
            </v-layout>
            <v-layout column align-center>
              <v-tooltip bottom>
                <v-btn
                  slot="activator"
                  icon
                  class="default-input primary--text text-none"
                  @click="addQuestionToCreate()"
                >
                  <v-icon> add </v-icon>
                </v-btn>
                <span>Add Item</span>
              </v-tooltip>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <sx-button-group :is-full-width="true" x-align="right">
            <div @click="openDialog = false">
              <sx-button type="secondary">
                {{ $t('cancel') }}
              </sx-button>
            </div>
            <div
              id="save-questionnaire-draft-btn"
              @click="sendQuestionnaires(true)"
            >
              <sx-button
                type="secondary"
                :is-disabled="!newQuestionnaireSavable || questionnaireSending"
                :is-loading="questionnaireSending"
              >
                Save Drafts
              </sx-button>
            </div>
            <div id="send-questionnaire-btn" @click="sendQuestionnaires(false)">
              <sx-button
                type="primary"
                :is-disabled="!newQuestionnaireSavable || questionnaireSending"
                :is-loading="questionnaireSending"
              >
                Send Document Drop Off
                <sx-icon :size="20" name="send" />
              </sx-button>
            </div>
          </sx-button-group>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-content>
</template>

<script>
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import {
  rules,
  templateFrequencyOptions,
  yearOptions,
} from '@/constants/variables';
import PriorYearItems from '@/components/PriorYearItems';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import SxIcon from '@/components/sx/SxIcon';
import SxButton from '@/components/sx/SxButton';
import SxButtonGroup from '@/components/sx/SxButtonGroup';

export default {
  name: 'SendDocumentDropOffDialog',
  mixins: [FirmPreferenceMixins],
  components: {
    PriorYearItems,
    SxButton,
    SxButtonGroup,
    SxIcon,
  },
  mixins: [FirmPreferenceMixins],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    currentCompany: {
      type: Object,
    },
    selectedEntityIds: {
      type: Array,
      default: null,
      optional: true,
    },
  },
  data() {
    return {
      rules,
      yearOptions,
      templateFrequencyOptions,
      addAdditionalQuestions: true,
      questionsToCreate: [],
      questionnaireSending: false,
      openDialog: false,
      selectedQuestionnaire: null,
      questionnaireDueDate: moment()
        .add(2, 'weeks')
        .format('Y-MM-DD'),
      questionnaireScheduledDate: moment()
        .add(1, 'days')
        .format('Y-MM-DD'),
      questionnaireDaysUntilDue: 14,
      selectedTemplateId: null,
      questionnaireYear: null,
      isScheduledTemplate: false,
      isBatchSending: false,
      updateProfileSection: false,
      signRequiredByAllOwner: false,
      depositRequired: false,
      depositAmount: '',
      includeRolloverItems: true,
      questionnaireTemplates: [],
      selectedFrequency: 'once',
      sectionsToInclude: [],
      entityIds: [],
      stripeConnected: false,
      refreshPriorYearItems: false,
      customTemplateName: 'Document Checklist',
      customEmailEnabled: false,
      customEmailSubject: '',
      customEmailBody: '',
    };
  },
  computed: {
    ...mapState('companies', ['companies', 'loadingCompanies']),
    ...mapGetters({
      currentUser: 'user',
      currentFirm: 'currentFirm',
    }),
    selectedEntity() {
      console.log(this.companies.find(c => c.id === this.entityIds[0]));
      return this.companies.find(c => c.id === this.entityIds[0]);
    },
    priorYearItemsTrigger() {
      return (
        !isEmpty(this.entityIds) &&
        !!this.includeRolloverItems &&
        !!this.questionnaireYear
      );
    },
    selectedQuestionnaireTemplate() {
      return this.questionnaireTemplates.find(
        q => q.id === this.selectedTemplateId,
      );
    },
    entitiesWithClient() {
      return this.companies
        .filter(
          c =>
            !c.inactive &&
            c.user &&
            c.user.paying &&
            (!this.currentUser.overview_setting.show_assigned_clients ||
              c.user.assigned_advisor_id === this.currentUser.id),
        )
        .map(entity => ({
          id: entity.id,
          name: this.getDisplayName(entity),
          entity_type: entity.entity_type,
          for_testing: entity.user ? entity.user.for_testing : false,
          email: entity.user ? entity.user.email : '',
          active: entity.user ? entity.user.paying : false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    entityOptionsWithoutAll() {
      return [...new Set(this.companies.map(c => c.entity_type))];
    },
    newQuestionnaireSavable() {
      return (
        !isEmpty(this.entityIds) &&
        this.questionnaireYear &&
        (!this.questionnaireDueDate ||
          moment() < moment(this.questionnaireDueDate))
      );
    },
    questionnaireYearPriorYearItems() {
      return this.companyPriorYearItems.filter(
        d => d.year === this.questionnaireYear,
      );
    },
    questionsToSave() {
      return this.questionsToCreate.filter(d => !isEmpty(d.text));
    },
    additionalQuestionSections() {
      return [
        ...this.selectedQuestionnaireTemplate.sections,
        {
          name: 'Additional Items',
          id: 0,
        },
      ];
    },
  },
  methods: {
    isEmpty,
    moment,
    addQuestionToCreate() {
      this.questionsToCreate.push({
        text: '',
        section_id: 0,
        question_type: 'upload',
        options: [],
        advanced: false,
      });
    },
    getDisplayName(entity) {
      if (this.isIndividual(entity)) {
        return entity.tax_return_name;
      } else {
        return `${entity.name} (${entity.user.full_name})`;
      }
    },
    isIndividual(entity) {
      return entity.entity_type === 'Individual';
    },
    selectSpecificEntityType(type) {
      if (type === 'All') {
        this.entityIds = this.entitiesWithClient.map(e => e.id);
      } else if (type === 'Test') {
        this.entityIds = this.entitiesWithClient
          .filter(e => e.for_testing)
          .map(e => e.id);
      } else {
        this.entityIds = this.entitiesWithClient
          .filter(e => e.entity_type === type)
          .map(e => e.id);
      }
    },
    reset() {
      if (!isEmpty(this.selectedEntityIds)) {
        this.entityIds = this.selectedEntityIds;
      } else {
        if (this.multiple) {
          this.entityIds = [];
        } else {
          this.entityIds = [this.currentCompany.id];
        }
      }
      this.questionnaireDueDate = moment()
        .add(2, 'weeks')
        .format('Y-MM-DD');
      this.questionnaireScheduledDate = moment()
        .add(1, 'days')
        .format('Y-MM-DD');
      this.questionnaireDaysUntilDue = 14;
      this.selectedTemplateId = null;
      this.questionnaireYear = null;
      this.isScheduledTemplate = false;
      this.signRequiredByAllOwner = false;
      this.depositRequired = false;
      this.depositAmount = '';
      this.includeRolloverItems = true;
      this.selectedFrequency = 'once';
      this.addAdditionalQuestions = true;
      this.questionsToCreate = [
        {
          text: '',
          question_type: 'upload',
          section_id: 0,
          options: [],
          advanced: false,
        },
      ];
      this.customTemplateName = 'Document Checklist';
      this.customEmailSubject = '';
      this.customEmailBody = '';
      this.customEmailEnabled = false;
    },
    async sendQuestionnaires(isDraft) {
      if (!this.newQuestionnaireSavable || this.questionnaireSending) return;
      if (this.isScheduledTemplate && isDraft) return;

      this.questionnaireSending = true;

      const questionsToCreate = this.questionsToSave.map(d => ({
        text: d.question_type === 'upload' ? `Please upload ${d.text}` : d.text,
        options: d.question_type === 'upload' ? [d.text] : [],
        section_id: d.section_id,
        question_type: d.question_type,
      }));

      try {
        const response1 = await API.questionnaire.sendQuestionnaires({
          companyIds: this.entityIds,
          questionnaireTemplateId: this.selectedTemplateId,
          taxYear: this.questionnaireYear,
          dueDate: this.questionnaireDueDate,
          frequency: this.isScheduledTemplate ? this.selectedFrequency : null,
          daysUntilDue: this.questionnaireDaysUntilDue,
          scheduledDate: this.questionnaireScheduledDate,
          includePriorYearItems: this.includeRolloverItems,
          requiredByAllOwners: this.signRequiredByAllOwner,
          depositRequired: this.depositRequired ? this.depositRequired : null,
          depositAmount: !isEmpty(this.depositAmount)
            ? this.depositAmount
            : null,
          updateProfile: this.updateProfileSection,
          questionsToCreate: this.addAdditionalQuestions
            ? questionsToCreate
            : [],
          batchSending: this.isBatchSending,
          batchReminder: this.isBatchSending,
          isDocumentPopoutChecklistEnabled: this.documentChecklistPopupEnabled,
          isDraft,
          documentDropOffEnabled: true,
          customTemplateName: this.customTemplateName,
          customEmailSubject: this.customEmailSubject,
          customEmailBody: this.customEmailBody,
        });

        if (response1.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response1.data.error,
          });
        } else {
          if (isDraft) {
            this.$store.dispatch('application/setSuccessNotification', {
              message: `Successfully saved Questionnaire draft.`,
            });
          } else if (this.isScheduledTemplate) {
            this.$store.dispatch('application/setSuccessNotification', {
              message: `You've successfully scheduled questionnaires.`,
            });
          } else {
            const {
              data: { message },
            } = response1;

            this.$store.dispatch('application/setSuccessNotification', {
              message,
            });
          }

          this.$emit('refresh-questionnaires');
          this.openDialog = false;
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.questionnaireSending = false;
      }
    },

    async checkStripeIntegration() {
      try {
        const response = await API.integration.checkStripeIntegration();

        this.stripeConnected = response.data.connected;
      } catch (error) {}
    },

    async getAllQuestionnaireTemplates() {
      const response = await API.questionnaireTemplate.getAllQuestionnaireTemplatesInfo();
      this.questionnaireTemplates = response.data;
    },
  },
  created() {
    this.openDialog = this.value;
  },
  watch: {
    value(newVal) {
      this.openDialog = newVal;
      if (this.openDialog) {
        this.reset();
        this.getAllQuestionnaireTemplates();
      }
    },

    openDialog(newVal) {
      this.$emit('input', newVal);

      if (newVal === true) {
        this.checkStripeIntegration();
      }
    },

    selectedTemplateId: {
      handler(value) {
        if (value && this.selectedQuestionnaireTemplate) {
          this.sectionsToInclude = this.selectedQuestionnaireTemplate.sections
            .sort((a, b) => a.order - b.order)
            .map(a => a.name);
        } else {
          this.sectionsToInclude = [];
        }
      },
    },
    priorYearItemsTrigger: {
      handler(value) {
        if (value && this.$refs.priorYearItems) {
          var child = this.$refs.priorYearItems;
          child.loadPriorYearItems();
        }
      },
    },
  },
};
</script>

<style scoped></style>
